import { DashboardCard } from '@/src/features/dashboard/presentation/components/DashboardCard'
import { appColors } from '@/src/theme/foundations/colors'
import IconLocationGroup from '@assets/icons/icon_location_group.svg?react'
import { Box, Divider, Flex, MenuItemOption, MenuList, MenuOptionGroup, Text, Tooltip } from '@chakra-ui/react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { FaRegBuilding } from 'react-icons/fa6'
import { MdOutlineLocationOn } from 'react-icons/md'
import { FlexCol } from '..'
import { SearchBox } from '../SearchBox/SearchBox'
import type { ILocationHierarchyOption } from './LocationMainSelect'

interface LocationSelectMenuListProps {
  isOpen: boolean
  activeSearchBoxId: number
  setActiveSearchBoxId: (id: number) => void
  onClick: (option: ILocationHierarchyOption) => void
  // regions
  regionOptions: ILocationHierarchyOption[]
  hoveredRegionIndex: number
  selectedRegionIndex: number
  regionSearchTerm: string
  handleRegionSearch: (value: string) => void
  handleRegionChange: (option: ILocationHierarchyOption) => void
  // locations
  locationOptions: ILocationHierarchyOption[]
  hoveredLocationIndex: number
  selectedLocationIndex: number
  locationSearchTerm: string
  showLocationList?: boolean
  handleLocationSearch: (value: string) => void
  handleLocationChange: (option: ILocationHierarchyOption) => void
  // buildings
  buildingOptions: ILocationHierarchyOption[]
  hoveredBuildingIndex: number
  selectedBuildingIndex: number
  buildingSearchTerm: string
  showBuildingList?: boolean
  handleBuildingSearch: (value: string) => void
  handleBuildingChange: (option: ILocationHierarchyOption) => void
}

export const LocationSelectMenuList = ({
  isOpen,
  activeSearchBoxId,
  setActiveSearchBoxId,
  onClick,
  // regions
  regionOptions,
  hoveredRegionIndex,
  selectedRegionIndex,
  regionSearchTerm,
  handleRegionSearch,
  handleRegionChange,
  // locations
  locationOptions,
  hoveredLocationIndex,
  selectedLocationIndex,
  locationSearchTerm,
  showLocationList = true,
  handleLocationSearch,
  handleLocationChange,
  // buildings
  buildingOptions,
  hoveredBuildingIndex,
  selectedBuildingIndex,
  buildingSearchTerm,
  showBuildingList = true,
  handleBuildingSearch,
  handleBuildingChange,
}: LocationSelectMenuListProps) => {
  const { t } = useTranslation()

  const isOptionAtHierarchy = (
    option: ILocationHierarchyOption,
    level: 'region' | 'location' | 'building',
    regionIndex: number,
    locationIndex: number,
    buildingIndex: number
  ) => {
    if (option.hierarchy.regionIndex !== regionIndex) return false
    if (level === 'region') return true
    if (option.hierarchy.locationIndex !== locationIndex) return false
    if (level === 'location') return true
    return option.hierarchy.buildingIndex === buildingIndex
  }

  const renderMenuGroup = (
    level: 'region' | 'location' | 'building',
    options: ILocationHierarchyOption[],
    onHover: (option: ILocationHierarchyOption) => void,
    onClick?: (option: ILocationHierarchyOption) => void
  ) => {
    const hoveredMenuKey = options.find(option =>
      isOptionAtHierarchy(option, level, hoveredRegionIndex, hoveredLocationIndex, hoveredBuildingIndex)
    )?.menuKey
    const selectedMenuKey = options.find(option =>
      isOptionAtHierarchy(option, level, selectedRegionIndex, selectedLocationIndex, selectedBuildingIndex)
    )?.menuKey

    return (
      <>
        <Box maxW='350px' width='20vw' maxH='340px' overflowY='auto' overflowX='hidden' className='innerScrollBar'>
          <MenuOptionGroup value={hoveredMenuKey}>
            {options.map(option => {
              const isSelected = option.menuKey === selectedMenuKey

              return (
                <Fragment key={option.menuKey}>
                  {option.showDividerBefore && <Divider my='4px' borderColor={appColors.border} />}
                  <MenuItemOption
                    value={option.menuKey}
                    closeOnSelect={true}
                    aria-selected={isSelected ? 'true' : 'false'}
                    onClick={() => onClick?.(option)}
                    color={isSelected ? appColors.white : appColors.foreground}
                    borderRadius='8px'
                    backgroundColor={isSelected ? appColors.accentPrimary : appColors.background}
                    _selected={{ backgroundColor: appColors.accentPrimary, fontWeight: 'bold', color: appColors.white }}
                    _hover={{
                      backgroundColor: isSelected ? appColors.accentPrimary : appColors.muted,
                      color: isSelected ? appColors.white : appColors.foreground,
                    }}
                    icon={null}
                    onMouseEnter={() => {
                      if (!isOpen) return
                      onHover(option)
                    }}
                  >
                    {option.label.length < 20 ? (
                      <Text fontSize='14px'>{option.label}</Text>
                    ) : (
                      <Tooltip hasArrow label={option.label} bg='white' color={appColors.foreground} fontSize={'14px'}>
                        <Text fontSize='14px'>{option.label}</Text>
                      </Tooltip>
                    )}
                  </MenuItemOption>
                </Fragment>
              )
            })}
          </MenuOptionGroup>
        </Box>
      </>
    )
  }

  const renderMenuRegionTitle = (icon: JSX.Element, title: string) => {
    return (
      <Flex width='20vw' maxWidth='350px' gap='4px' alignItems='center' p={'8px'}>
        {icon}
        <Text fontSize={14} color={appColors.foreground} fontWeight='bold'>
          {title}
        </Text>
      </Flex>
    )
  }

  return (
    <MenuList zIndex={100} style={{ backgroundColor: 'transparent', border: 'none', padding: 0, boxShadow: 'none' }}>
      <Flex gap='10px'>
        {/* REGIONS */}
        <DashboardCard padding={3} onClick={() => setActiveSearchBoxId(0)}>
          <FlexCol gap='6px'>
            {renderMenuRegionTitle(<IconLocationGroup />, t('user_management.groups'))}
            <SearchBox value={regionSearchTerm} id={0} focusId={activeSearchBoxId} onChange={handleRegionSearch} />
            {renderMenuGroup('region', regionOptions, handleRegionChange, onClick)}
          </FlexCol>
        </DashboardCard>

        {/* LOCATIONS */}
        {showLocationList && (
          <DashboardCard padding={3} onClick={() => setActiveSearchBoxId(1)}>
            <FlexCol width='20vw' maxWidth='350px' gap='6px'>
              {renderMenuRegionTitle(<MdOutlineLocationOn color={appColors.secondary} />, t('common.locations'))}
              <SearchBox
                width='20vw'
                maxWidth='350px'
                value={locationSearchTerm}
                id={1}
                focusId={activeSearchBoxId}
                onChange={handleLocationSearch}
              />
              {renderMenuGroup('location', locationOptions, handleLocationChange, onClick)}
            </FlexCol>
          </DashboardCard>
        )}

        {/* BUILDINGS */}
        {showBuildingList && (
          <DashboardCard padding={3} onClick={() => setActiveSearchBoxId(2)}>
            <FlexCol width='20vw' maxWidth='350px' gap='6px'>
              {renderMenuRegionTitle(<FaRegBuilding color={appColors.secondary} />, 'Gebäude')}
              <SearchBox
                width='20vw'
                maxWidth='350px'
                value={buildingSearchTerm}
                id={2}
                focusId={activeSearchBoxId}
                onChange={handleBuildingSearch}
              />
              {renderMenuGroup('building', buildingOptions, handleBuildingChange, onClick)}
            </FlexCol>
          </DashboardCard>
        )}
      </Flex>
    </MenuList>
  )
}
